import { useStoreCameraViewSession, useStoreCameras, useStoreDashboardStats } from '@gozoki/api'
import type { StoreCamera } from '@gozoki/api-types'
import { frenchFormat } from '@gozoki/tools'
import { Skeleton } from '@gozoki/ui/components/ui/skeleton'
import { cn } from '@gozoki/ui/lib/utils'
import { useEffect, useState } from 'react'
import bottomWaveOrange from '~/assets/dashboard/bottom-wave-orange.svg'
import { CamerasModal } from './CamerasModal'
import { Camera01Icon, ChartUpIcon, TradeUpIcon } from './icons/dashboard-icons'
import { CardDivider, CardTitle, DashboardCard, EmptyState, TrendBadge } from './ui/DashboardCard'

const ratioToPercent = (ratio: number | null | undefined): string =>
  ratio == null ? '—' : `${(ratio * 100).toFixed(1).replace('.', ',')} %`

// Ligne de score (Figma 10715:55916) : pastille de variation, libellé, valeur.
function ScoreRow({
  trend,
  label,
  value,
  trendSuffix = '%',
  danger = false,
}: {
  trend: number | null | undefined
  label: string
  value: string
  trendSuffix?: string
  danger?: boolean
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-[8px] rounded-[16px] border p-[13px]',
        danger ? 'border-[#FBC6C6] bg-[#FEECEC]' : 'border-[#B8D9A8] bg-[#E4EDDB]',
      )}
    >
      <span
        className={cn(
          'flex shrink-0 items-center gap-[2px]',
          danger ? 'text-[#F25757]' : 'text-[#4F6A47]',
        )}
      >
        <span
          className={cn(
            'flex size-[20px] items-center justify-center',
            (trend ?? 0) < 0 && 'rotate-x-180',
          )}
        >
          <TradeUpIcon />
        </span>
        <span className="font-sans text-[12px] leading-[14px] font-semibold">
          {trend == null ? '—' : `${trend}${trendSuffix}`}
        </span>
      </span>
      <span
        className={cn(
          'min-w-0 flex-1 truncate font-sans text-[14px] leading-[16px] font-semibold',
          danger ? 'text-[#F25757]' : 'text-[#4F6A47]',
        )}
      >
        {label}
      </span>
      <span className="shrink-0 font-sans text-[16px] leading-[18px] font-semibold text-[#354A31]">
        {value}
      </span>
    </div>
  )
}

// Figma 10715:55889 — C.A sur 7 jours + scores opérationnels.
export function CaTotalCard() {
  const { data, isLoading } = useStoreDashboardStats()
  const chart = data?.caChart
  const scores = data?.scores

  return (
    <DashboardCard className="h-[313px] shrink-0 border-[#0000000F] bg-[#CEE3C4]">
      {/* Modale CA 7 jours en attente de refonte (Lucie, 08/10) : rebrancher `CaDetailModal`. */}
      <CardTitle
        icon={<ChartUpIcon />}
        iconBoxClassName="bg-[#B8D9A8] text-[#4F6A47] size-[40px]"
        title="C.A"
        titleClassName="text-[#354A31]"
        meta="7 JOURS"
        metaClassName="text-[#354A31]"
      />

      <CardDivider />

      <div className="flex h-[36px] items-center gap-[12px]">
        {isLoading ? (
          <Skeleton className="h-[36px] w-[160px]" />
        ) : (
          <span className="min-w-0 flex-1 font-brand text-[32px] leading-[36px] font-extrabold text-[#354A31]">
            {chart ? `${frenchFormat(Math.round(chart.totalCa))}€` : '—'}
          </span>
        )}
        <TrendBadge value={chart?.vsPrevWeekPercent ?? null} className="text-[#354A31]" />
      </div>

      <div className="flex flex-col gap-[8px]">
        <ScoreRow
          trend={scores?.fifoTrend != null ? Math.round(scores.fifoTrend) : null}
          trendSuffix=""
          label="Score FIFO"
          value={scores?.fifoScore != null ? `${Math.round(scores.fifoScore)} / 100` : '—'}
        />
        <ScoreRow
          trend={scores?.casseTrend != null ? Math.round(scores.casseTrend * 1000) / 10 : null}
          label="Taux de casse"
          value={ratioToPercent(scores?.tauxCasse)}
        />
        <ScoreRow
          trend={scores?.volTrend != null ? Math.round(scores.volTrend * 1000) / 10 : null}
          danger
          label="Taux de vol"
          value={ratioToPercent(scores?.tauxVol)}
        />
      </div>
    </DashboardCard>
  )
}

function withToken(url: string, token: string): string {
  const u = new URL(url)
  u.searchParams.set('token', token)
  return u.toString()
}

const CAMERA_TILE_COUNT = 3

// Vignette caméra du widget. La snapshot est tirée du worker : si celui-ci rejette la frame
// (ex. 401 → ORB quand le runtime pointe hors env), l'image cassée est masquée et la tuile
// retombe sur son fond uni + libellé. L'erreur est réarmée à chaque nouveau src.
function CameraWidgetTile({
  camera,
  token,
  onOpen,
}: {
  camera: StoreCamera
  token: string
  onOpen: () => void
}) {
  const src = camera.snapshotUrl ? withToken(camera.snapshotUrl, token) : null
  const [errored, setErrored] = useState(false)
  useEffect(() => setErrored(false), [src])

  return (
    <button
      type="button"
      onClick={onOpen}
      disabled={!camera.fullStreamUrl}
      className="relative flex min-h-0 flex-1 items-end overflow-hidden rounded-[8px] bg-[#2F3D28] disabled:opacity-50"
    >
      {src && !errored ? (
        <img
          src={src}
          alt={camera.label}
          onError={() => setErrored(true)}
          className="absolute inset-0 size-full object-cover"
        />
      ) : null}
      <span className="relative z-10 m-[8px] rounded-full bg-black/40 px-[8px] py-[2px] font-sans text-[12px] text-white">
        {camera.label}
      </span>
    </button>
  )
}

function PlaceholderTiles() {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-[16px]">
      {Array.from({ length: CAMERA_TILE_COUNT }).map((_, index) => (
        <div key={index} className="min-h-0 flex-1 rounded-[8px] bg-[#E5EBE5]" />
      ))}
    </div>
  )
}

// Figma 10715:56012 — trois vignettes caméra empilées.
export function CamerasCard() {
  const { data: cameras, isLoading } = useStoreCameras()
  const hasCameras = (cameras?.length ?? 0) > 0
  const { data: session } = useStoreCameraViewSession(hasCameras)
  // `null` = modale fermée ; une chaîne vide ouvre la grille, un nom ouvre directement ce flux.
  const [openedOn, setOpenedOn] = useState<string | null>(null)

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="flex min-h-0 flex-1 flex-col gap-[16px]">
          {Array.from({ length: CAMERA_TILE_COUNT }).map((_, index) => (
            <Skeleton key={index} className="min-h-0 flex-1 rounded-[8px]" />
          ))}
        </div>
      )
    }

    if (!hasCameras || !session) return <PlaceholderTiles />

    return (
      <div className="flex min-h-0 flex-1 flex-col gap-[16px]">
        {(cameras ?? []).slice(0, CAMERA_TILE_COUNT).map((camera) => (
          <CameraWidgetTile
            key={camera.id}
            camera={camera}
            token={session.token}
            onOpen={() => setOpenedOn(camera.name)}
          />
        ))}
      </div>
    )
  }

  return (
    <DashboardCard className="min-h-0 flex-1 border-[#0000000F] bg-[#F3F6F3]">
      <CardTitle
        icon={<Camera01Icon />}
        iconBoxClassName="bg-[#E5EBE5] text-[#4F6A47] size-[40px]"
        title="CAMERAS"
        titleClassName="text-[#4F6A47]"
        onOpen={() => setOpenedOn('')}
        openLabel="Voir toutes les caméras"
      />

      <CardDivider />

      {renderBody()}

      {openedOn !== null ? (
        <CamerasModal
          cameras={cameras ?? []}
          session={session}
          initialCameraName={openedOn || null}
          onClose={() => setOpenedOn(null)}
        />
      ) : null}
    </DashboardCard>
  )
}

/**
 * Figma 10715:62926 — SATISFACTION CLIENT.
 *
 * ⚠️ NON BRANCHÉ : les avis Google ne sont remontés nulle part (ni API Places, ni table, ni
 * endpoint boutique). La carte montre un état vide plutôt que le « 4,7 » et le « + 8 % vs S-1 »
 * de la maquette : une note sur 5 ne se distingue pas d'une vraie, c'est le placeholder le plus
 * trompeur des quatre.
 * Quand la source existera : note en Bricolage 40/44 alignée à droite, variation + « vs S-1 » à
 * sa gauche (Montserrat 16 bold / 14 medium), mention « Note moyenne sur Google » en dessous.
 */
export function SatisfactionClientCard() {
  return (
    <div
      className="relative flex h-[198px] shrink-0 flex-col justify-between overflow-hidden rounded-[24px] border border-[#D7D7D7] p-[17px]"
      style={{ backgroundImage: 'linear-gradient(-44.17deg, #E58C11 37.811%, #EFA031 62.189%)' }}
    >
      {/* La texture exportée par Figma est vide : on reprend le dégradé et la vague du design. */}
      <img
        src={bottomWaveOrange}
        alt=""
        aria-hidden
        className="pointer-events-none absolute bottom-[-90px] left-[-120px] w-[560px] max-w-none opacity-60"
      />

      <span className="relative font-brand text-[22px] leading-[26px] font-semibold text-[#F9F8F1]">
        SATISFACTION
        <br />
        CLIENT
      </span>

      <EmptyState className="text-[#F9F8F1]">Note client non disponible</EmptyState>
    </div>
  )
}
